import React, { useEffect, useState } from "react";
import { FiPlay, FiX } from "react-icons/fi";
import { curatedGallery } from "./homePreviewData.js";
import ScrollReveal from "./ScrollReveal.jsx";
import ListingMediaPreview from "../ListingMediaPreview.jsx";

function VideoLightbox({ item, onClose }) {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
      >
        <FiX size={22} />
      </button>
      <div className="w-full max-w-3xl" onClick={(event) => event.stopPropagation()}>
        <video
          src={item.video}
          poster={item.image}
          controls
          autoPlay
          playsInline
          className="max-h-[80vh] w-full rounded-2xl bg-black shadow-2xl"
        />
        <div className="mt-4 text-center text-white">
          <h3 className="font-display text-xl font-bold">{item.title}</h3>
          <p className="mt-1 text-sm text-white/70">{item.caption}</p>
        </div>
      </div>
    </div>
  );
}

export default function HomePreviewGallery() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section id="preview-gallery" className="bg-[linear-gradient(180deg,#FDFBF7_0%,#ffffff_100%)] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <ScrollReveal className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-zoom-gold">Recent setups</p>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-zoom-emeraldDeep sm:text-[44px] sm:leading-tight">
              Recent DecorZoom Work
            </h2>
          </div>
          <p className="max-w-md text-sm font-normal leading-7 text-zoom-charcoal/64">
            Real setups, real celebrations, ready for your next event.
          </p>
        </ScrollReveal>

        <div className="mt-10 grid auto-rows-[250px] gap-5 md:grid-cols-2 lg:grid-cols-4">
          {curatedGallery.map((item, index) => (
            <ScrollReveal
              key={item.title}
              delay={index * 0.06}
              className={`group relative overflow-hidden rounded-3xl border border-zoom-emerald/10 bg-white shadow-hp-lg transition-shadow duration-300 hover:shadow-hp-glow ${
                item.size === "large" ? "md:col-span-2 md:row-span-2" : ""
              }`}
            >
              <div
                className={`relative h-full w-full overflow-hidden ${item.video ? "cursor-pointer" : ""}`}
                onClick={() => item.video && setActiveVideo(item)}
                role={item.video ? "button" : undefined}
                tabIndex={item.video ? 0 : undefined}
                onKeyDown={(event) => {
                  if (item.video && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    setActiveVideo(item);
                  }
                }}
              >
                {item.video ? (
                  <ListingMediaPreview
                    videoSrc={item.video}
                    posterSrc={item.image}
                    alt={item.title}
                    className="h-full w-full [&_img]:scale-100 [&_img]:transition-transform [&_img]:duration-700 [&_img]:ease-out group-hover:[&_img]:scale-110 [&_video]:scale-100 [&_video]:transition-transform [&_video]:duration-700 [&_video]:ease-out group-hover:[&_video]:scale-110"
                  />
                ) : (
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="h-full w-full scale-100 object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                )}

                {item.video && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/25 text-white shadow-hp-sm backdrop-blur-sm transition group-hover:bg-white/35">
                      <FiPlay size={22} className="ml-0.5" />
                    </span>
                  </div>
                )}

                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(38,35,34,0)_38%,rgba(10,61,58,0.88)_100%)] opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 translate-y-1 p-5 transition-transform duration-300 group-hover:translate-y-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-zoom-goldSoft">Look {index + 1}</p>
                  <h3 className="font-display mt-2 text-xl font-bold text-white">{item.title}</h3>
                  <p className="mt-1 text-sm font-normal leading-6 text-white/75">{item.caption}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {activeVideo && <VideoLightbox item={activeVideo} onClose={() => setActiveVideo(null)} />}
    </section>
  );
}
